import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import { getMatch, getStages, getScores, createStage, updateStage, updateMatch, uploadStageImage, addStageToMatch } from '../../api';
import Snackbar from '../../components/Snackbar';
import { StageDetail } from '../match/Stages';

export default function Stages() {
  const { matchId } = useParams();
  const { t } = useTranslation();
  const [stages, setStages] = useState([]);
  const [stageIds, setStageIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);
  const [current, setCurrent] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  const [dragIndex, setDragIndex] = useState(null);
  const [pendingImage, setPendingImage] = useState(null);
  const [pendingImagePreview, setPendingImagePreview] = useState(null);
  const [scoredWarning, setScoredWarning] = useState(false);
  const [hasScores, setHasScores] = useState(false);
  const [pendingReorder, setPendingReorder] = useState(null);
  const [previewStage, setPreviewStage] = useState(null);

  const newStage = useCallback(() => ({
    matchId,
    title: '',
    targets: { paper: 0, poppers: 0 },
    rounds: 0,
    startPosition: t('stages.defaultStartPosition', 'Anywhere in the designated area.'),
    gunCondition: '',
    pccCondition: '',
    start: t('stages.defaultStart', 'Audible'),
    end: t('stages.defaultEnd', 'Last round'),
    procedure: t('stages.defaultProcedure', 'After start signal engage all targets from designated area.'),
    comments: '',
    bonusTargets: [],
    inactive: false,
    shouldSplit: false,
  }), [t, matchId]);

  const HANDGUN_TARGET_FIELDS = [
    { key: 'paper', label: t('stages.paperTargets', 'Paper Targets') },
    { key: 'minipaper', label: t('stages.miniPaper', 'Mini Paper') },
    { key: 'poppers', label: t('stages.poppers', 'Poppers') },
    { key: 'minipoppers', label: t('stages.miniPoppers', 'Mini Poppers') },
    { key: 'plates', label: t('stages.plates', 'Plates') },
  ];

  const PCC_TARGET_FIELDS = [
    { key: 'pcc-ten-points-poppers', label: t('stages.tenPtPoppers', '10-pt Poppers') },
    { key: 'pcc-frangible', label: t('stages.pccFrangible', 'PCC Frangible') },
    { key: 'pcc-ten-points-frangible', label: t('stages.tenPtFrangible', '10-pt Frangible') },
  ];

  const loadStages = useCallback(async () => {
    try {
      setLoading(true);
      const { data: matchData } = await getMatch(matchId);
      const ids = matchData.stages || [];
      setStageIds(ids);
      if (ids.length > 0) {
        const [stagesRes, scoresRes] = await Promise.all([
          getStages(ids.join(',')),
          getScores(matchId).catch(() => ({ data: { scores: [] } })),
        ]);
        setStages(Array.isArray(stagesRes.data) ? stagesRes.data : [stagesRes.data]);
        const scores = scoresRes.data?.scores || [];
        setHasScores(scores.length > 0);
      } else {
        setStages([]);
        setHasScores(false);
      }
    } catch (err) {
      setSnack({ open: true, message: t('stages.loadFailed', 'Failed to load stages'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => { loadStages(); }, [loadStages]);

  const handleAdd = () => {
    setEditIndex(-1);
    setCurrent(newStage());
    setPendingImage(null);
    setPendingImagePreview(null);
    setDialogOpen(true);
  };

  const handleEdit = (idx) => {
    setEditIndex(idx);
    setCurrent({ ...stages[idx], targets: { ...stages[idx].targets }, bonusTargets: [...(stages[idx].bonusTargets || [])] });
    setPendingImage(null);
    setPendingImagePreview(null);
    setDialogOpen(true);
  };

  const handleDialogImageSelect = (file) => {
    if (!file) return;
    setPendingImage(file);
    const url = URL.createObjectURL(file);
    setPendingImagePreview(url);
  };

  const handleTargetChange = (key, value) => {
    const num = parseInt(value, 10) || 0;
    setCurrent((prev) => {
      const newTargets = { ...prev.targets, [key]: num };
      // Auto-calc rounds if they currently match
      const oldCalc = calcRounds(prev.targets);
      const newCalc = calcRounds(newTargets);
      const autoUpdate = prev.rounds === oldCalc;
      return { ...prev, targets: newTargets, rounds: autoUpdate ? newCalc : prev.rounds };
    });
  };

  const calcRounds = (tgts) => {
    return ((tgts.paper || 0) + (tgts.minipaper || 0)) * 2 +
      (tgts.poppers || 0) + (tgts.minipoppers || 0) + (tgts.plates || 0) +
      (tgts['pcc-frangible'] || 0) + (tgts['pcc-ten-points-poppers'] || 0) + (tgts['pcc-ten-points-frangible'] || 0);
  };

  const handleSaveStage = async ({ force } = {}) => {
    setSaving(true);
    setScoredWarning(false);
    try {
      let stageId;
      if (editIndex >= 0) {
        stageId = stageIds[editIndex];
        const { id, ...stageData } = current;
        await updateStage(stageId, stageData, force ? { force: true } : undefined);
      } else {
        const res = await createStage(current);
        stageId = res.data.stageId;
        await addStageToMatch(matchId, stageId);
      }
      // Upload pending image if selected
      if (pendingImage && stageId) {
        const formData = new FormData();
        formData.append('file', pendingImage);
        await uploadStageImage(stageId, formData);
      }
      await loadStages();
      setDialogOpen(false);
      setPendingImage(null);
      setPendingImagePreview(null);
      setSnack({ open: true, message: t('common.saveSuccess'), severity: 'success' });
    } catch (err) {
      if (err.response?.status === 403) {
        setScoredWarning(true);
      } else {
        setSnack({ open: true, message: err.response?.data?.error || t('common.saveError'), severity: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (idx) => {
    setSaving(true);
    try {
      // Strip server-added `id` — the Stage schema forbids extra properties
      // (otherwise: "stage must NOT have additional properties (id)").
      const { id, ...stageData } = stages[idx];
      await updateStage(stageIds[idx], { ...stageData, inactive: true });
      await loadStages();
      setConfirmDelete(null);
      setSnack({ open: true, message: t('stages.stageDeactivated', 'Stage deactivated'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: err.response?.data?.error || t('stages.deleteError', 'Cannot delete stage with scores'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (stageId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await uploadStageImage(stageId, formData);
      await loadStages();
      setSnack({ open: true, message: t('stages.imageUploaded', 'Image uploaded'), severity: 'success' });
    } catch (err) {
      setSnack({ open: true, message: t('stages.imageUploadFailed', 'Image upload failed'), severity: 'error' });
    }
  };

  const handleDragStart = (idx) => setDragIndex(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (idx) => {
    if (dragIndex === null || dragIndex === idx) return;
    const newStagesList = [...stages];
    const newIds = [...stageIds];
    const [movedStage] = newStagesList.splice(dragIndex, 1);
    const [movedId] = newIds.splice(dragIndex, 1);
    newStagesList.splice(idx, 0, movedStage);
    newIds.splice(idx, 0, movedId);
    setDragIndex(null);
    if (hasScores) {
      setPendingReorder({ stages: newStagesList, ids: newIds });
    } else {
      applyReorder(newStagesList, newIds);
    }
  };

  const applyReorder = async (newStagesList, newIds, force = false) => {
    setStages(newStagesList);
    setStageIds(newIds);
    setPendingReorder(null);
    try {
      await updateMatch(matchId, { stages: newIds }, force ? { force: true } : undefined);
    } catch (err) {
      setSnack({ open: true, message: t('stages.reorderFailed', 'Failed to save stage order'), severity: 'error' });
      await loadStages();
    }
  };

  const toggleBonusTarget = (index) => {
    setCurrent((prev) => {
      const bonus = prev.bonusTargets || [];
      return {
        ...prev,
        bonusTargets: bonus.includes(index)
          ? bonus.filter((i) => i !== index)
          : [...bonus, index],
      };
    });
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  const numPaperTargets = current ? (current.targets?.paper || 0) + (current.targets?.minipaper || 0) : 0;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">{t('matches.stages')}</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>{t('stages.addStage', 'Add Stage')}</Button>
      </Box>

      {stages.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="text.secondary">{t('scoring.noStages1')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{t('scoring.noStages2')}</Typography>
          <Button variant="contained" onClick={handleAdd}>{t('scoring.noStagesCta')}</Button>
        </Box>
      )}

      {stages.map((stage, idx) => (
        <Card
          key={stageIds[idx] || idx}
          sx={{ mb: 1.5, opacity: stage.inactive ? 0.5 : 1 }}
          draggable
          onDragStart={() => handleDragStart(idx)}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop(idx)}
        >
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 0 }}>
            <DragIndicatorIcon sx={{ cursor: 'grab', color: 'text.disabled' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {t('stages.stage')} {idx + 1}{stage.title ? `: ${stage.title}` : ''}
                {stage.inactive && <Chip label={t('stages.inactive')} size="small" color="default" sx={{ ml: 1 }} />}
                {stage.shouldSplit && <Chip label={t('stages.hotRange', 'Hot Range')} size="small" color="warning" sx={{ ml: 1 }} />}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {stage.rounds} {t('stages.rounds')} | {t('stages.paperTargets', 'Paper')}: {stage.targets?.paper || 0} | {t('stages.metal', 'Metal')}: {(stage.targets?.poppers || 0) + (stage.targets?.minipoppers || 0) + (stage.targets?.plates || 0)}
              </Typography>
            </Box>
            {stage.img && (
              <Box
                component="img"
                src={stage.img}
                sx={{ width: 80, height: 60, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                onClick={() => window.open(stage.img, '_blank')}
              />
            )}
          </CardContent>
          <CardActions>
            <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setPreviewStage({ stage, index: idx })}>{t('common.view')}</Button>
            <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(idx)}>{t('common.edit', 'Edit')}</Button>
            <Button size="small" component="label" startIcon={<ImageIcon />}>
              {t('stages.image', 'Image')}
              <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload(stageIds[idx], e.target.files[0])} />
            </Button>
            <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setConfirmDelete(idx)}>{t('common.delete')}</Button>
          </CardActions>
        </Card>
      ))}

      {/* Edit/Add Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editIndex >= 0 ? t('stages.editStage', 'Edit Stage') : t('stages.addStage', 'Add Stage')}</DialogTitle>
        <DialogContent>
          {current && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label={t('stages.title', 'Title')} value={current.title || ''} onChange={(e) => setCurrent({ ...current, title: e.target.value })} fullWidth />

            {/* Handgun Targets */}
            <Typography variant="subtitle2">{t('stages.targets')}</Typography>
            <Grid container spacing={2}>
              {HANDGUN_TARGET_FIELDS.map((tf) => (
                <Grid key={tf.key} size={{ xs: 6 }}>
                  <TextField
                    label={tf.label}
                    type="number"
                    value={current.targets?.[tf.key] || 0}
                    onChange={(e) => handleTargetChange(tf.key, e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>

            {/* PCC Targets */}
            <Typography variant="subtitle2">{t('stages.pccTargets', 'PCC Targets')}</Typography>
            <Grid container spacing={2}>
              {PCC_TARGET_FIELDS.map((tf) => (
                <Grid key={tf.key} size={{ xs: 6 }}>
                  <TextField
                    label={tf.label}
                    type="number"
                    value={current.targets?.[tf.key] || 0}
                    onChange={(e) => handleTargetChange(tf.key, e.target.value)}
                    fullWidth
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>

            {/* No-Shoots */}
            <Typography variant="subtitle2">{t('stages.noShoots', 'No-Shoots')}</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label={t('stages.paperNoShoots', 'Paper No-Shoots')}
                  type="number"
                  value={current.targets?.['paper-no-shoots'] || 0}
                  onChange={(e) => handleTargetChange('paper-no-shoots', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 6 }}>
                <TextField
                  label={t('stages.metalNoShoots', 'Metal No-Shoots')}
                  type="number"
                  value={current.targets?.['metal-no-shoots'] || 0}
                  onChange={(e) => handleTargetChange('metal-no-shoots', e.target.value)}
                  fullWidth
                  size="small"
                />
              </Grid>
            </Grid>

            {/* Rounds */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label={t('stages.rounds')}
                type="number"
                value={current.rounds || 0}
                onChange={(e) => setCurrent({ ...current, rounds: parseInt(e.target.value, 10) || 0 })}
                sx={{ width: 150 }}
              />
              <Button size="small" variant="outlined" onClick={() => setCurrent((prev) => ({ ...prev, rounds: calcRounds(prev.targets) }))}>{t('stages.autoCalc', 'Auto Calculate')}</Button>
            </Box>

            {/* Bonus Targets */}
            {numPaperTargets > 0 && (
              <Box>
                <Typography variant="subtitle2">{t('stages.bonusTargets')}</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {Array.from({ length: numPaperTargets }, (_, i) => (
                    <FormControlLabel
                      key={i}
                      control={<Checkbox size="small" checked={(current.bonusTargets || []).includes(i)} onChange={() => toggleBonusTarget(i)} />}
                      label={`T${i + 1}`}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <TextField label={t('stages.startPosition')} value={current.startPosition || ''} onChange={(e) => setCurrent({ ...current, startPosition: e.target.value })} fullWidth multiline />
            <TextField label={t('stages.gunCondition')} value={current.gunCondition || ''} onChange={(e) => setCurrent({ ...current, gunCondition: e.target.value })} fullWidth multiline />
            <TextField label={t('stages.pccCondition')} value={current.pccCondition || ''} onChange={(e) => setCurrent({ ...current, pccCondition: e.target.value })} fullWidth multiline />
            <TextField label={t('stages.start')} value={current.start || ''} onChange={(e) => setCurrent({ ...current, start: e.target.value })} fullWidth multiline />
            <TextField label={t('stages.procedure')} value={current.procedure || ''} onChange={(e) => setCurrent({ ...current, procedure: e.target.value })} fullWidth multiline rows={3} />
            <TextField label={t('stages.end')} value={current.end || ''} onChange={(e) => setCurrent({ ...current, end: e.target.value })} fullWidth multiline />
            <TextField label={t('stages.comments')} value={current.comments || ''} onChange={(e) => setCurrent({ ...current, comments: e.target.value })} fullWidth multiline rows={2} />

            {/* Image upload & preview */}
            <Box>
              <Button variant="outlined" component="label" size="small" startIcon={<ImageIcon />}>
                {(current.img || pendingImagePreview) ? t('stages.changeImage', 'Change Image') : t('stages.uploadImage', 'Upload Image')}
                <input type="file" hidden accept="image/*" onChange={(e) => handleDialogImageSelect(e.target.files[0])} />
              </Button>
              {(pendingImagePreview || current.img) && (
                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Box
                    component="img"
                    src={pendingImagePreview || current.img}
                    sx={{ maxWidth: '100%', maxHeight: 200, borderRadius: 1, cursor: 'pointer' }}
                    onClick={() => window.open(pendingImagePreview || current.img, '_blank')}
                  />
                </Box>
              )}
            </Box>
            <FormControlLabel
              control={<Switch checked={!!current.shouldSplit} onChange={(e) => setCurrent({ ...current, shouldSplit: e.target.checked })} />}
              label={t('stages.hotRangeLabel')}
            />
            <FormControlLabel
              control={<Switch checked={!!current.inactive} onChange={(e) => setCurrent({ ...current, inactive: e.target.checked })} />}
              label={t('stages.inactiveLabel', 'Cancelled stage: not counted for scoring')}
            />
          </Box>
          )}
        </DialogContent>
        {scoredWarning && (
          <Alert severity="warning" sx={{ mx: 3, mt: 1, mb: 1 }} action={
            <Button color="warning" size="small" onClick={() => handleSaveStage({ force: true })} disabled={saving}>
              {t('stages.saveAnyway', 'Save anyway')}
            </Button>
          }>
            {t('stages.scoredWarning', 'Scores have already been submitted for this stage. You should delete scores before changing it.')}
          </Alert>
        )}
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setScoredWarning(false); }}>{t('common.cancel')}</Button>
          <Button onClick={handleSaveStage} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>{t('stages.deleteStage', 'Delete Stage?')}</DialogTitle>
        <DialogContent>
          <Typography>{t('stages.deleteStageConfirm', 'This will mark the stage as inactive. Stages with existing scores cannot be fully deleted.')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</Button>
          <Button onClick={() => handleDelete(confirmDelete)} color="error" variant="contained" disabled={saving}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reorder confirmation when scores exist */}
      <Dialog open={pendingReorder !== null} onClose={() => setPendingReorder(null)}>
        <DialogTitle>{t('stages.reorderWarningTitle', 'Reorder Stages?')}</DialogTitle>
        <DialogContent>
          <Typography>{t('stages.reorderWarning', 'Scores have already been submitted. Reordering stages will break the association between scores and stages.')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingReorder(null)}>{t('common.cancel')}</Button>
          <Button onClick={() => applyReorder(pendingReorder.stages, pendingReorder.ids, true)} color="warning" variant="contained">
            {t('stages.reorderAnyway', 'Reorder anyway')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Stage Preview Dialog */}
      <Dialog open={!!previewStage} onClose={() => setPreviewStage(null)} maxWidth="md" fullWidth>
        {previewStage && (
          <>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {t('stages.stage')} {previewStage.index + 1}{previewStage.stage.title ? ` - ${previewStage.stage.title}` : ''}
              <IconButton onClick={() => setPreviewStage(null)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent>
              <StageDetail stage={previewStage.stage} index={previewStage.index} hideTitle hideOpenImage />
            </DialogContent>
          </>
        )}
      </Dialog>

      <Snackbar open={snack.open} message={snack.message} severity={snack.severity} onClose={() => setSnack({ ...snack, open: false })} />
    </Container>
  );
}
